<!--
  Project home — the pour list, a New pour action, and job setup.

  The list is the answer to "is anything half finished?", so every row states
  what is outstanding rather than just a tick. A failed Hold point is called out
  in red on the row itself: it is the one thing that stops a pour being closed.

  Crew also get the client-link panel here, because handing a client a link is
  the spec's stated secondary purpose and this is the only screen above a
  single pour.

  "Export all pours" is the Excel workbook, not the CSV: it carries every table
  the CSV does plus the photographs from every pour, in the sheet. The
  /export/pours.csv route remains for anything automated. The same file, and the
  per-pour exports, are also reachable from the Export panel on a pour.
-->
<script lang="ts">
  import { api, exportUrl, isApiError } from '../lib/api';
  import { linkHandler, navigate, routePath } from '../lib/router';
  import {
    canEdit,
    loadPours,
    notifyError,
    notify,
    pourOutstanding,
    pours,
    poursLoading,
    project,
    todayIso
  } from '../lib/store';
  import type { PourSummary, ShareTokenView } from '../lib/types';

  let creating = $state(false);
  let showNewPour = $state(false);
  let newLabel = $state('');
  let newDate = $state(todayIso());
  let prefillFrom = $state<number | null>(null);
  let createError = $state<string | null>(null);

  // Share tokens (crew only).
  let shareOpen = $state(false);
  let shareTokens = $state<ShareTokenView[]>([]);
  let shareLoaded = $state(false);
  let shareBusy = $state(false);
  let shareLabel = $state('');
  let copiedTokenId = $state<number | null>(null);

  $effect(() => {
    void loadPours();
  });

  function openNewPour() {
    showNewPour = true;
    createError = null;
    newDate = todayIso();
    if (!newLabel) newLabel = suggestLabel($pours);
    // Default the prefill source to the most recent pour: "confirm rather than
    // retype" is a requirement, not a nicety.
    if (prefillFrom === null && $pours.length > 0) prefillFrom = $pours[0].id;
  }

  /** 'Pile 1' -> 'Pile 2'. Falls back to a plain date label. */
  function suggestLabel(list: PourSummary[]): string {
    const last = list[0]?.label ?? '';
    const match = /^(.*?)(\d+)\s*$/.exec(last);
    if (match) return `${match[1]}${Number(match[2]) + 1}`;
    return '';
  }

  async function createPour(event: SubmitEvent) {
    event.preventDefault();
    if (creating) return;
    const label = newLabel.trim();
    if (!label) {
      createError = 'Give the pour a label, e.g. "Pile 1".';
      return;
    }

    creating = true;
    createError = null;
    try {
      const response = await api.createPour({
        label,
        date: newDate,
        prefill_from_pour_id: prefillFrom
      });
      showNewPour = false;
      newLabel = '';
      await loadPours();
      navigate(routePath.pour(response.pour.pour.id));
    } catch (cause) {
      createError = isApiError(cause) ? cause.message : 'Could not create the pour.';
    } finally {
      creating = false;
    }
  }

  async function openShare() {
    shareOpen = !shareOpen;
    if (!shareOpen || shareLoaded) return;
    try {
      const response = await api.listShareTokens();
      shareTokens = response.share_tokens;
      shareLoaded = true;
    } catch (cause) {
      notifyError(cause, 'Could not load the client links');
    }
  }

  async function createShareToken(event: SubmitEvent) {
    event.preventDefault();
    const label = shareLabel.trim();
    if (!label || shareBusy) return;
    shareBusy = true;
    try {
      const response = await api.createShareToken({ label });
      shareTokens = [response.share_token, ...shareTokens];
      shareLabel = '';
    } catch (cause) {
      notifyError(cause, 'Could not create the client link');
    } finally {
      shareBusy = false;
    }
  }

  async function revoke(token: ShareTokenView) {
    shareBusy = true;
    try {
      const response = await api.revokeShareToken(token.id);
      shareTokens = shareTokens.map((item) =>
        item.id === token.id ? response.share_token : item
      );
    } catch (cause) {
      notifyError(cause, 'Could not revoke the link');
    } finally {
      shareBusy = false;
    }
  }

  async function copy(token: ShareTokenView) {
    try {
      await navigator.clipboard.writeText(token.share_url);
      copiedTokenId = token.id;
      setTimeout(() => (copiedTokenId = null), 2500);
    } catch {
      notify({
        tone: 'info',
        title: 'Copy the link by hand',
        detail: token.share_url,
        sticky: true
      });
    }
  }

  function choosePrefill(event: Event) {
    const raw = (event.currentTarget as HTMLSelectElement).value;
    prefillFrom = raw === '' ? null : Number(raw);
  }

  function statusPill(pour: PourSummary): { text: string; cls: string } {
    if (pour.open_failed_hold_points > 0) {
      return {
        text: `${pour.open_failed_hold_points} hold point${pour.open_failed_hold_points === 1 ? '' : 's'} failed`,
        cls: 'pill--hold'
      };
    }
    if (pour.status === 'closed') return { text: 'Closed', cls: 'pill--ok' };
    if (pour.status === 'open') return { text: 'Open', cls: 'pill--warn' };
    return { text: 'Draft', cls: 'pill--pending' };
  }
</script>

<h1 class="pagetitle">{$project?.name ?? 'Project'}</h1>
<p class="meta client">{$project?.client ?? ''}</p>

<div class="btnrow toprow">
  <a class="btn" href={routePath.jobSetup()} onclick={linkHandler}>Job setup</a>
  <a class="btn" href={exportUrl.projectXlsx()} download>Export all pours (Excel)</a>
</div>

{#if $canEdit}
  {#if showNewPour}
    <form class="card" onsubmit={createPour}>
      <div class="card__body">
        <h2>New pour</h2>
        {#if createError}
          <div class="banner banner--error" role="alert"><div>{createError}</div></div>
        {/if}
        <div class="grid2">
          <label class="field">
            <span class="label">Label</span>
            <input
              type="text"
              bind:value={newLabel}
              autocapitalize="words"
              enterkeyhint="next"
              placeholder="Pile 1"
            />
          </label>
          <label class="field">
            <span class="label">Date</span>
            <input type="date" bind:value={newDate} />
          </label>
        </div>

        {#if $pours.length > 0}
          <label class="field">
            <span class="label">Pre-fill from</span>
            <select
              value={prefillFrom === null ? '' : String(prefillFrom)}
              onchange={choosePrefill}
            >
              <option value="">Start blank</option>
              {#each $pours as pour (pour.id)}
                <option value={String(pour.id)}>{pour.label} — {pour.date}</option>
              {/each}
            </select>
            <span class="field__hint">
              Copies operators, unit no, water source and calibration settings. Chute tests,
              stop-hold events, photos and checklist statuses are never copied.
            </span>
          </label>
        {/if}

        <div class="btnrow">
          <button class="btn btn--primary btn--big" type="submit" disabled={creating}>
            {#if creating}<span class="spinner" aria-hidden="true"></span> Creating…{:else}Create
              pour{/if}
          </button>
          <button class="btn" type="button" onclick={() => (showNewPour = false)}>Cancel</button>
        </div>
      </div>
    </form>
  {:else}
    <button class="btn btn--primary btn--block btn--big newbtn" type="button" onclick={openNewPour}>
      + New pour
    </button>
  {/if}
{/if}

<h2 class="listtitle">Pours</h2>

{#if $poursLoading && $pours.length === 0}
  <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if $pours.length === 0}
  <div class="card">
    <div class="card__body meta">
      No pours recorded yet.{#if $canEdit}
        Start one with <em>New pour</em>.{/if}
    </div>
  </div>
{:else}
  <div class="card">
    <ul class="list">
      {#each $pours as pour (pour.id)}
        {@const status = statusPill(pour)}
        <li>
          <a class="list__item pourrow" href={routePath.pour(pour.id)} onclick={linkHandler}>
            <div class="row row--between">
              <strong class="pourrow__label">{pour.label}</strong>
              <span class="pill {status.cls}">{status.text}</span>
            </div>
            <div class="meta">{pour.date} · {pourOutstanding(pour)}</div>
            <div class="meta counts">
              {pour.chute_test_count} chute test{pour.chute_test_count === 1 ? '' : 's'} ·
              {pour.stop_hold_event_count} stop-hold ·
              {pour.photo_count} photo{pour.photo_count === 1 ? '' : 's'} ·
              ITP {pour.itp_recorded_count}/{pour.itp_total_count}
            </div>
          </a>
        </li>
      {/each}
    </ul>
  </div>
{/if}

{#if $canEdit}
  <details class="section" open={shareOpen}>
    <summary
      onclick={(event) => {
        event.preventDefault();
        void openShare();
      }}
    >
      <span class="section__label">Client links</span>
      <span class="pill pill--pending">{shareTokens.filter((t) => t.active).length} active</span>
    </summary>
    <div class="section__body">
      <p class="meta">
        A client link is read and export only. Revoking one stops it working immediately.
      </p>

      <form class="row shareform" onsubmit={createShareToken}>
        <input
          type="text"
          bind:value={shareLabel}
          placeholder="Issued to (e.g. Kiewit QC)"
          autocapitalize="words"
        />
        <button class="btn btn--primary" type="submit" disabled={shareBusy}>Create link</button>
      </form>

      {#if shareTokens.length === 0 && shareLoaded}
        <p class="meta">No client links issued.</p>
      {/if}

      <ul class="list tokens">
        {#each shareTokens as token (token.id)}
          <li class="token">
            <div class="row row--between">
              <strong>{token.label}</strong>
              <span class="pill {token.active ? 'pill--ok' : 'pill--pending'}">
                {token.active ? 'Active' : 'Revoked'}
              </span>
            </div>
            {#if token.active}
              <div class="mono meta tokenurl">{token.share_url}</div>
              <div class="btnrow">
                <button class="btn btn--sm" type="button" onclick={() => copy(token)}>
                  {copiedTokenId === token.id ? 'Copied' : 'Copy link'}
                </button>
                <button
                  class="btn btn--sm btn--danger"
                  type="button"
                  disabled={shareBusy}
                  onclick={() => revoke(token)}
                >
                  Revoke
                </button>
              </div>
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  </details>
{/if}

<style>
  .pagetitle {
    margin-top: 0.25rem;
  }

  .client {
    margin-bottom: 0.9rem;
  }

  .toprow {
    margin-bottom: var(--gap);
  }

  .newbtn {
    margin-bottom: var(--gap);
  }

  .listtitle {
    margin: 1.25rem 0 0.5rem;
  }

  .pourrow {
    display: block;
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }

  .pourrow__label {
    font-size: 1.1rem;
  }

  .counts {
    font-size: 0.78rem;
  }

  .shareform {
    flex-wrap: nowrap;
    gap: 0.5rem;
    margin-bottom: var(--gap);
  }

  .shareform input {
    flex: 1 1 auto;
  }

  .tokens .token {
    border-bottom: 1px solid var(--line-soft);
    padding: 0.6rem 0;
  }

  .tokens .token:last-child {
    border-bottom: 0;
  }

  .tokenurl {
    font-size: 0.72rem;
    word-break: break-all;
    margin: 0.25rem 0 0.5rem;
  }
</style>
